"use client"

import { useEffect, useState, useRef, useCallback, useMemo } from "react"
import Button from "../modules/Button/Button"
import styles from "./styles.module.css"
import Tabs from "../modules/Tabs/Tabs"
import AddBox from "./add-box"
import AddDevices from "./add-devices"
import AddIndividualDevice from "./add-individual"
import { FiUploadCloud } from "react-icons/fi"
import Modal from "../modules/Modal/Moadal"
import UploadCSV from "./upload-csv"
import { bulkCreateWarehouseDevices, createWarehouseBox } from "../../../services/inventory/detail"
import * as XLSX from "xlsx"
import toast from "react-hot-toast"
import { parseDateSafe } from "../../../utils/dateFormatters"

const CATEGORIES = [
  {
    id: 1,
    name: "Scale",
    compatible_device_types: [
      { id: 1, name: "Bariatric Scale" },
      { id: 8, name: "Tenovi Cellular Scale" },
      { id: 15, name: "Tenovi Hub" },
    ],
  },
  {
    id: 2,
    name: "BPM",
    compatible_device_types: [
      { id: 2, name: "Tenovi BPM - L" },
      { id: 3, name: "Tenovi BPM - S" },
      { id: 4, name: "Tenovi Cellular BPM - L" },
      { id: 5, name: "Tenovi Cellular BPM - S" },
      { id: 16, name: "Omron Wrist BPM - 7 Series" },
      { id: 15, name: "Tenovi Hub" },
    ],
  },
  {
    id: 3,
    name: "Glucometer",
    compatible_device_types: [
      { id: 6, name: "Tenovi Cellular Glucometer" },
      { id: 9, name: "Tenovi Glucometer" },
      { id: 15, name: "Tenovi Hub" },
    ],
  },
  {
    id: 4,
    name: "Pulse Ox",
    compatible_device_types: [
      { id: 7, name: "Tenovi Cellular Pulse Ox" },
      { id: 11, name: "Tenovi Pulse Ox" },
      { id: 15, name: "Tenovi Hub" },
    ],
  },
  {
    id: 5,
    name: "Thermometer",
    compatible_device_types: [
      { id: 13, name: "Tenovi Thermometer" },
      { id: 15, name: "Tenovi Hub" },
    ],
  },
  {
    id: 6,
    name: "Peak Flow Meter",
    compatible_device_types: [
      { id: 10, name: "Tenovi Peak Flow Meter" },
      { id: 15, name: "Tenovi Hub" },
    ],
  },
  {
    id: 7,
    name: "Watch",
    compatible_device_types: [
      { id: 14, name: "Tenovi Watch" },
      { id: 15, name: "Tenovi Hub" },
    ],
  },
  {
    id: 8,
    name: "Multiple",
    compatible_device_types: [
      { id: 1, name: "Bariatric Scale" },
      { id: 2, name: "Tenovi BPM - L" },
      { id: 3, name: "Tenovi BPM - S" },
      { id: 4, name: "Tenovi Cellular BPM - L" },
      { id: 5, name: "Tenovi Cellular BPM - S" },
      { id: 6, name: "Tenovi Cellular Glucometer" },
      { id: 7, name: "Tenovi Cellular Pulse Ox" },
      { id: 8, name: "Tenovi Cellular Scale" },
      { id: 9, name: "Tenovi Glucometer" },
      { id: 10, name: "Tenovi Peak Flow Meter" },
      { id: 11, name: "Tenovi Pulse Ox" },
      { id: 12, name: "Tenovi Scale" },
      { id: 13, name: "Tenovi Thermometer" },
      { id: 14, name: "Tenovi Watch" },
      { id: 15, name: "Tenovi Hub" },
      { id: 16, name: "Omron Wrist BPM - 7 Series" },
    ],
  },
]

const CATEGORY_BY_NAME = new Map(CATEGORIES.map((c) => [String(c.name).toLowerCase(), c]))
const CATEGORY_BY_ID = new Map(CATEGORIES.map((c) => [String(c.id), c]))
const DEVICE_NAME_TO_ID = new Map()
const DEVICE_NAME_TO_CATEGORY_ID = new Map()
CATEGORIES.forEach((c) => {
  c.compatible_device_types.forEach((d) => {
    DEVICE_NAME_TO_ID.set(String(d.name).toLowerCase(), d.id)
    DEVICE_NAME_TO_CATEGORY_ID.set(String(d.name).toLowerCase(), c.id)
  })
})

const TABS = [
  { id: 1, label: "Add Individual" },
  { id: 2, label: "Add Bulk" },
]

// limit per created box when importing template
const MAX_DEVICES_PER_BOX = 15

const AddInventory = ({ isOpen, setIsOpen, onInventoryUpdate }) => {
  const [addType, setAddType] = useState(1)
  const [showAddBox, setShowAddBox] = useState(false)
  const [boxes, setBoxes] = useState([])
  const [visibleBoxes, setVisibleBoxes] = useState([])
  const [boxDetails, setBoxDetails] = useState("")
  const [allBoxDevices, setAllBoxDevices] = useState({})
  const [bulkLoading, setBulkLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")
  const [showAddMoreBoxesModal, setShowAddMoreBoxesModal] = useState(false)
  const [showAddMoreDevicesModal, setShowAddMoreDevicesModal] = useState(false)
  const [deviceErrors, setDeviceErrors] = useState([])
  const [addDeviceLoading, setAddDeviceLoading] = useState(false)
  const [addMoreBoxesLoading, setAddMoreBoxesLoading] = useState(false)
  const [addMoreDevicesLoading, setAddMoreDevicesLoading] = useState(false)
  const [showCsvModal, setShowCsvModal] = useState(false)
  const addIndividualRef = useRef()

  const mountedRef = useRef(false)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"

      if ((boxes?.length || 0) === 0) setShowAddBox(true)
    } else {
      document.body.style.overflow = "auto"
    }
    return () => {
      document.body.style.overflow = "auto"
    }
  }, [isOpen, boxes.length])

  const setBox = useCallback((box) => {
    setBoxes((prev) => {
      const newBoxNo = prev.length > 0 ? Math.max(...prev.map((b) => b.box_no || b.id)) + 1 : 1
      const boxed = { ...box, box_no: box.box_no || newBoxNo, updatedAt: Date.now() }
      return [...prev, boxed]
    })
    setShowAddBox(false)
  }, [])

  const updateBox = useCallback((box) => {
    setBoxes((prev) => {
      const updated = prev.map((_box) => (_box.id === box.id ? { ...box, updatedAt: Date.now() } : _box))
      return [...updated]
    })
    setBoxDetails("")
    setShowAddBox(false)
  }, [])

  const setUpdateBox = useCallback((box) => {
    setBoxDetails(box)
    setShowAddBox(true)
  }, [])

  const handleDevicesChange = useCallback((boxId, devices) => {
    const normalized = Array.isArray(devices)
      ? devices.map((d) => {
          const serial = d.serial ?? d.serial_number ?? ""

          let device_type = d.device_type ?? d.device_type_id ?? null
          if (device_type && typeof device_type === "string") {
            const id = DEVICE_NAME_TO_ID.get(device_type.toLowerCase())
            device_type = id ?? device_type
          }
          return { ...d, serial, device_type }
        })
      : []

    setAllBoxDevices((prev) => {
      const prevForBox = prev[boxId] || []
      const same =
        prevForBox.length === normalized.length &&
        prevForBox.length > 0 &&
        JSON.stringify(prevForBox) === JSON.stringify(normalized)
      if (same) return prev
      return { ...prev, [boxId]: normalized }
    })

    setDeviceErrors([])
  }, [])

  const getTotalDeviceCount = useCallback(() => {
    return Object.values(allBoxDevices).reduce((total, devices) => total + devices.length, 0)
  }, [allBoxDevices])

  const headerButtons = useMemo(() => {
    if (addType !== 2) return []
    return [
      {
        label: "Upload CSV / XLSX",
        icon: <FiUploadCloud />,
        onClick: () => setShowCsvModal(true),
        variant: "default",
      },
      {
        label: "Download Template",
        onClick: () => {
          const link = document.createElement("a")
          link.href = "/Device_Configuration_Template.xlsx"
          link.download = "Device_Configuration_Template.xlsx"
          link.click()
        },
        variant: "default",
      },
    ]
  }, [addType])

  const handleBulkCreateDevices = useCallback(async () => {
    const totalDevices = getTotalDeviceCount()
    if (totalDevices === 0) return

    setBulkLoading(true)
    setDeviceErrors([])

    try {
      const payload = []
      Object.entries(allBoxDevices).forEach(([boxId, devices]) => {
        devices.forEach((d) => {
          payload.push({
            serial: d.serial,
            device_type: Number(d.device_type) || d.device_type,
            inventory_status: d.inventory_status,
            box_id: Number.parseInt(boxId),
          })
        })
      })

      const result = await bulkCreateWarehouseDevices(payload)

      if (result && result.success) {
        setSuccessMessage(
          `Successfully created ${totalDevices} device${totalDevices > 1 ? "s" : ""} across ${boxes.length} box${
            boxes.length > 1 ? "es" : ""
          }!`,
        )

        setShowAddMoreBoxesModal(true)
        setAllBoxDevices({})
        setBoxes([])
        if (onInventoryUpdate) {
          onInventoryUpdate()
        }
      } else if (result && !result.success) {
        if (result.errors && Array.isArray(result.errors)) setDeviceErrors(result.errors)
        toast.error("Failed to create some devices. Please review errors.")
      }
    } catch (error) {
      console.error("Error creating devices:", error)
      toast.error("Error creating devices. See console for details.")
    } finally {
      setBulkLoading(false)
    }
  }, [allBoxDevices, boxes.length, getTotalDeviceCount, onInventoryUpdate])

  const handleIndividualDeviceSuccess = useCallback(
    (deviceData) => {
      setSuccessMessage(`Device "${deviceData.serial}" added successfully!`)
      toast.success(`Device "${deviceData.serial}" added successfully!`)
      setShowAddMoreDevicesModal(true)
      if (onInventoryUpdate) {
        onInventoryUpdate()
      }
    },
    [onInventoryUpdate],
  )

  const handleAddDevice = useCallback(async () => {
    if (addType === 1 && addIndividualRef.current) {
      setAddDeviceLoading(true)
      try {
        await addIndividualRef.current.submitForm()
      } finally {
        setAddDeviceLoading(false)
      }
    }
  }, [addType])

  useEffect(() => {
    if (boxes.length === 1) {
      setVisibleBoxes([boxes[0].box_no || boxes[0].id])
    }
  }, [boxes.length, boxes])

  const parseFileToRows = useCallback(
    (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader()
        const isCsv = file.name.toLowerCase().endsWith(".csv")
        reader.onload = (evt) => {
          try {
            if (isCsv) {
              const csvText = evt.target.result
              const workbook = XLSX.read(csvText, { type: "string" })
              const sheetName = workbook.SheetNames[0]
              const worksheet = workbook.Sheets[sheetName]
              const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" })
              resolve(jsonData)
            } else {
              const data = new Uint8Array(evt.target.result)
              const workbook = XLSX.read(data, { type: "array" })
              const sheetName = workbook.SheetNames[0]
              const worksheet = workbook.Sheets[sheetName]
              const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" })
              resolve(jsonData)
            }
          } catch (err) {
            reject(err)
          }
        }
        reader.onerror = (err) => reject(err)
        if (isCsv) reader.readAsText(file)
        else reader.readAsArrayBuffer(file)
      }),
    [],
  )

  const handleTemplateParsed = useCallback(async (rows) => {
    if (!rows || rows.length === 0) {
      toast("No rows found in uploaded file.")
      return
    }

    setBulkLoading(true)
    setDeviceErrors([])

    try {

      const rowsWithBox = rows.filter((r) => r.box_number && String(r.box_number).trim() !== "")
      if (rowsWithBox.length === 0) {
        toast.error("Template must include at least one row with 'box_number'.")
      setShowCsvModal(false)

        setBulkLoading(false)
        return
      }

      const grouped = rowsWithBox.reduce((acc, r) => {
        const bn = String(r.box_number).trim()
        if (!acc[bn]) acc[bn] = []
        acc[bn].push(r)
        return acc
      }, {})

      const createdBoxes = []

      for (const boxNumber of Object.keys(grouped)) {
        const boxRows = grouped[boxNumber]

        // split into chunks of MAX_DEVICES_PER_BOX
        for (let chunkIndex = 0; chunkIndex * MAX_DEVICES_PER_BOX < boxRows.length; chunkIndex++) {
          const start = chunkIndex * MAX_DEVICES_PER_BOX
          const end = start + MAX_DEVICES_PER_BOX
          const chunkRows = boxRows.slice(start, end)

          const firstRow = chunkRows[0]

          const rawCategory = (firstRow.box_device_type ?? firstRow.device_type ?? "").toString().trim()
          let categoryId = null
          if (rawCategory) {
            const asNum = Number(rawCategory)
            if (Number.isFinite(asNum) && CATEGORY_BY_ID.has(String(asNum))) categoryId = asNum
            else {
              const found = CATEGORY_BY_NAME.get(rawCategory.toLowerCase())
              if (found) categoryId = found.id
            }
          }

          // if chunkIndex > 0, append suffix to box_number to avoid duplicates
          const boxNumberToCreate = chunkIndex === 0 ? String(boxNumber) : `${String(boxNumber)}-${chunkIndex + 1}`

          const boxPayload = {
            box_number: boxNumberToCreate,
            box_type: firstRow.box_type || "new",
            device_type: categoryId,
            inventory_status: firstRow.inventory_status || "In Warehouse",
            added_at: firstRow.added_at ? (parseDateSafe(firstRow.added_at)?.toISOString() || new Date().toISOString()) : new Date().toISOString(),
          }

          const boxRes = await createWarehouseBox(boxPayload)
          if (!boxRes || boxRes.success === false) {
            console.error("Failed to create box:", boxPayload, boxRes)
            toast.error(`Failed to create box ${boxNumberToCreate}. See console for details.`)
            continue
          }

          const createdBoxId =
            boxRes.id || (boxRes.data && (boxRes.data.id || boxRes.data.pk)) || boxRes.pk || boxRes.box_id || Date.now()

          const boxForUI = {
            id: createdBoxId,
            box_number: boxPayload.box_number,
            box_device_type_name: boxPayload.box_device_type_name || rawCategory || "",
            box_type: boxPayload.box_type,
            inventory_status: boxPayload.inventory_status,
            compatible_device_types: boxRes.compatible_device_types || boxRes.data?.compatible_device_types || [],
            devices: [],
          }

          const deviceRowsForBox = chunkRows.filter((r) => r.serial && String(r.serial).trim() !== "")

          const devicePayload = deviceRowsForBox.map((row) => {
            const serial = String(row.serial).trim()
            let deviceTypeRaw = row.device_type ?? row.box_device_type ?? ""
            let device_type_id = null
            if (deviceTypeRaw) {
              const asNum = Number(deviceTypeRaw)
              if (Number.isFinite(asNum)) device_type_id = asNum
              else {
                const foundId = DEVICE_NAME_TO_ID.get(String(deviceTypeRaw).toLowerCase())
                if (foundId) device_type_id = foundId
                else device_type_id = null
              }
            }
            return {
              serial,
              device_type: device_type_id,
              inventory_status: row.inventory_status || boxPayload.inventory_status || "In Warehouse",
            }
          })

          createdBoxes.push({ boxNumber: boxNumberToCreate, createdBoxId, boxForUI, devicePayload })
        }
      }

      setBoxes((prev) => {
        const next = [...prev]
        createdBoxes.forEach((b) => next.push({ ...b.boxForUI, updatedAt: Date.now() }))
        return next
      })

      setAllBoxDevices((prev) => {
        const next = { ...prev }
        createdBoxes.forEach((b) => {
          next[b.createdBoxId] = b.devicePayload
        })
        return next
      })

      setAddType(2)
      setShowCsvModal(false)
      setShowAddBox(false)
      setVisibleBoxes((prev) => {
        const next = [...prev]
        createdBoxes.forEach((b) => {
          if (!next.includes(b.createdBoxId)) next.push(b.createdBoxId)
        })
        return next
      })

      if (createdBoxes.length === 0) {
        toast.error("No boxes were created from the template. Check console for details.")
      } else {
        toast.success(`Created ${createdBoxes.length} box(es) and loaded their devices into preview. Review then click 'Add All Devices'.`)
      }
    } catch (err) {
      console.error("Error processing uploaded template:", err)
      toast.error("Upload failed. See console for details.")
    } finally {
      setBulkLoading(false)
    }
  }, [setBoxes, setAllBoxDevices, setAddType, setShowCsvModal, setShowAddBox, setVisibleBoxes])

  const handleFileUpload = useCallback(
    async (file) => {
      try {
        setShowCsvModal(true)
        const rows = await parseFileToRows(file)
        await handleTemplateParsed(rows)
      } catch (err) {
        console.error("Failed to parse uploaded file:", err)
        toast.error("Failed to parse uploaded file.")
      }
    },
    [parseFileToRows, handleTemplateParsed],
  )

  const sortedBoxes = useMemo(() => boxes.slice().sort((a, b) => (a.box_no || a.id) - (b.box_no || b.id)), [boxes])
  const visibleSorted = useMemo(
    () => sortedBoxes.filter((b) => visibleBoxes.includes(b.box_no || b.id) || visibleBoxes.includes(b.id)),
    [sortedBoxes, visibleBoxes],
  )

  return (
    <>
      <Modal
        title="Add Devices"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        width="60%"
        height="85vh"
        headerButtons={headerButtons}
        closeOnOverlayClick={false}
        footer={
          <>
            <Button variant="secondary" size="small" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            {addType === 1 ? (
              <Button size="small" onClick={handleAddDevice} loading={addDeviceLoading} disabled={addDeviceLoading}>
                Add Device
              </Button>
            ) : (
              <Button size="small" onClick={handleBulkCreateDevices} disabled={bulkLoading} loading={bulkLoading}>
                {`Add All Devices${getTotalDeviceCount() > 0 ? ` (${getTotalDeviceCount()})` : ""}`}
              </Button>
            )}
          </>
        }
      >
        <div className={styles.tabs}>
          <Tabs tabs={TABS} activeTab={addType} onChange={(id) => setAddType(id)} />
        </div>

        <section>
          {addType === 1 ? (
            <AddIndividualDevice ref={addIndividualRef} onSuccess={handleIndividualDeviceSuccess} />
          ) : (
            <section>
              {showAddBox && (
                <AddBox
                  setBox={setBox}
                  updateDetails={boxDetails}
                  updateBox={updateBox}
                  boxCount={boxes.length}
                  handleBoxVisibility={setShowAddBox}
                  onRefresh={onInventoryUpdate}
                />
              )}

              {boxes.length > 0 && <section className={styles.showDevicesContainer}></section>}

              {visibleSorted.map((b) => (
                <AddDevices
                  key={`${b.id}-${b.updatedAt || 0}`}
                  boxDetails={{ ...b }}
                  previewDevices={allBoxDevices[b.id] || allBoxDevices[b.box_number] || b.devices || []}
                  updateBox={setUpdateBox}
                  onDevicesChange={handleDevicesChange}
                  deviceErrors={deviceErrors}
                />
              ))}
            </section>
          )}
        </section>
      </Modal>

      {}
      <Modal
        title="Upload CSV / XLSX Template"
        isOpen={showCsvModal}
        onClose={() => setShowCsvModal(false)}
        width="500px"
        height="auto"
      >
        <div style={{ padding: 12 }}>
          <p style={{ marginBottom: 12 }}>
            Upload the filled template (CSV or Excel). First row containing <strong>box_number</strong> will be used to
            create the box. Rows containing <strong>serial</strong> will be loaded into the preview (they will only be
            created when you click "Add All Devices").
          </p>

          <div style={{ marginBottom: 12 }}>
            <UploadCSV onFileUpload={handleFileUpload} />
          </div>

          <div style={{ marginTop: 12 }}>
            <a
              href="/Device_Configuration_Template.xlsx"
              download="Device_Configuration_Template.xlsx"
              target="_blank"
              rel="noreferrer"
            >
              Download example template
            </a>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
              Template headers expected: <code>box_number, box_type, box_device_type, serial, device_type, inventory_status, added_at</code>
            </div>
          </div>
        </div>
      </Modal>

      {}
      <Modal
        title="Devices Added Successfully!"
        isOpen={showAddMoreBoxesModal}
        onClose={() => {
          setShowAddMoreBoxesModal(false)
          setIsOpen(false)
        }}
        width="500px"
        height="auto"
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <p style={{ fontSize: "16px", fontWeight: "500", color: "#374151", marginBottom: "30px" }}>
            Do you want to add more boxes?
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            <Button variant="primary" size="small" onClick={() => {
              setShowAddMoreBoxesModal(false)
              setIsOpen(true)
              setAddType(2)
              setShowAddBox(true)
            }}>
              Add More Boxes
            </Button>
            <Button variant="secondary" size="small" onClick={() => { setShowAddMoreBoxesModal(false); setIsOpen(false) }}>
              Finish
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        title="Device Added Successfully!"
        isOpen={showAddMoreDevicesModal}
        onClose={() => { setShowAddMoreDevicesModal(false); setIsOpen(false) }}
        width="500px"
        height="auto"
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <p style={{ fontSize: "16px", fontWeight: "500", color: "#374151", marginBottom: "30px" }}>
            Do you want to add more devices?
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            <Button variant="primary" size="small" onClick={() => { setShowAddMoreDevicesModal(false); setShowAddBox(true); setAddType(2) }} loading={addMoreDevicesLoading}>
              Add More Devices
            </Button>
            <Button variant="secondary" size="small" onClick={() => { setShowAddMoreDevicesModal(false); setIsOpen(false) }}>
              Finish
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default AddInventory
